class AcWishlistStore extends EventTarget {
  #storageKey = 'argue-culture-wishlist-v2';
  #legacyStorageKey = 'argue-culture-wishlist-v1';
  #hookSelector = '[data-ac-wishlist-toggle], [data-ac-wishlist-count]';
  #observer;
  #pendingRoots = new Set();
  #hydrationQueued = false;

  constructor() {
    super();
    this.#migrate();
    document.addEventListener('click', this.#handleClick);
    document.addEventListener('ac:wishlist:updated', this.sync);
    document.addEventListener('ac:wishlist:preview', this.#handlePreview);
    window.addEventListener('storage', this.#handleStorage);
    this.#observe();
    this.sync();
  }

  get items() {
    return this.#read();
  }

  find(productId) {
    return this.#read().find((item) => item.productId === String(productId || '')) || null;
  }

  remove(productId) {
    const id = String(productId || '');
    return this.#write(this.#read().filter((item) => item.productId !== id));
  }

  sync = (root = document) => {
    const hydrationRoot = this.#isHydrationRoot(root) ? root : document;
    const items = this.#read();
    this.#hydrate(hydrationRoot, items);
  };

  #hydrate(root, items) {
    this.#nodes(root, '[data-ac-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
    });
    this.#nodes(root, '[data-ac-wishlist-toggle]').forEach((button) => {
      const active = items.some((item) => item.productId === String(button.dataset.acWishlistProductId || button.dataset.acWishlistId || ''));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = active ? button.dataset.acWishlistRemoveLabel : button.dataset.acWishlistAddLabel;
      if (label) button.setAttribute('aria-label', label);
      const visibleLabel = button.querySelector('[data-ac-wishlist-label]');
      const visibleText = active
        ? button.dataset.acWishlistRemoveText || button.dataset.acWishlistRemoveLabel
        : button.dataset.acWishlistAddText || button.dataset.acWishlistAddLabel;
      if (visibleLabel && visibleText) visibleLabel.textContent = visibleText;
    });
  }

  #observe() {
    if (!document.documentElement || typeof MutationObserver === 'undefined') return;
    this.#observer = new MutationObserver(this.#handleMutations);
    this.#observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  #handleMutations = (records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (!node.matches(this.#hookSelector) && !node.querySelector(this.#hookSelector)) continue;
        this.#pendingRoots.add(node);
      }
    }
    if (!this.#pendingRoots.size || this.#hydrationQueued) return;

    this.#hydrationQueued = true;
    queueMicrotask(() => {
      this.#hydrationQueued = false;
      const roots = [...this.#pendingRoots];
      this.#pendingRoots.clear();
      const items = this.#read();
      for (const root of roots) {
        if (!root.isConnected) continue;
        if (roots.some((candidate) => candidate !== root && candidate.contains(root))) continue;
        this.#hydrate(root, items);
      }
    });
  };

  #nodes(root, selector) {
    const nodes = [];
    if (root instanceof Element && root.matches(selector)) nodes.push(root);
    if ('querySelectorAll' in root) nodes.push(...root.querySelectorAll(selector));
    return nodes;
  }

  #isHydrationRoot(root) {
    return root instanceof Document || root instanceof DocumentFragment || root instanceof Element;
  }

  #handleClick = (event) => {
    const button = event.target.closest?.('[data-ac-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const item = this.#fromButton(button);
    if (!item.productId) return;

    const items = this.#read();
    const index = items.findIndex((saved) => saved.productId === item.productId);
    const removing = index >= 0;
    if (removing) items.splice(index, 1);
    else items.unshift(item);

    if (!this.#write(items)) {
      this.#announce(button.dataset.acWishlistErrorMessage || 'Favorites are unavailable');
      return;
    }

    button.classList.remove('is-feedback');
    requestAnimationFrame(() => button.classList.add('is-feedback'));
    window.setTimeout(() => button.classList.remove('is-feedback'), 460);

    const message = removing ? button.dataset.acWishlistRemovedMessage : button.dataset.acWishlistAddedMessage;
    if (button.dataset.acWishlistFeedback !== 'false' && message) this.#announce(message);
  };

  #handlePreview = (event) => {
    const button = event.detail?.button;
    if (!(button instanceof HTMLElement)) return;

    const item = this.#fromButton(button);
    const items = this.#read();
    const index = items.findIndex((saved) => saved.productId === item.productId);
    if (index < 0) return;
    items[index] = item;
    this.#write(items);
  };

  #handleStorage = (event) => {
    if (event.key !== this.#storageKey) return;
    this.sync();
    this.#notify();
  };

  #read() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.#storageKey) || '[]');
      return Array.isArray(saved) ? saved.map((item) => this.#normalize(item)).filter((item) => item.productId) : [];
    } catch (error) {
      return [];
    }
  }

  #write(items) {
    try {
      const normalized = items.map((item) => this.#normalize(item)).filter((item) => item.productId);
      window.localStorage.setItem(this.#storageKey, JSON.stringify(normalized));
      this.sync();
      this.#notify(normalized);
      return true;
    } catch (error) {
      return false;
    }
  }

  #notify(items = this.#read()) {
    const event = new CustomEvent('ac:wishlist:changed', { detail: { items } });
    document.dispatchEvent(event);
    this.dispatchEvent(event);
  }

  #normalize(item = {}) {
    const legacyId = String(item.id || '');
    const productId = String(item.productId || legacyId.split(':')[0] || legacyId);
    const variantId = String(item.variantId || legacyId.split(':')[1] || '');
    return {
      id: productId,
      productId,
      variantId,
      title: String(item.title || ''),
      variantTitle: String(item.variantTitle || ''),
      options: Array.isArray(item.options)
        ? item.options.filter((option) => option?.value && option.value !== 'Default Title')
        : [],
      url: String(item.url || ''),
      image: String(item.image || ''),
      price: String(item.price || ''),
      available: item.available !== false,
    };
  }

  #fromButton(button) {
    const names = this.#parse(button.dataset.acWishlistOptionNames);
    const values = this.#parse(button.dataset.acWishlistOptionValues);
    return this.#normalize({
      id: button.dataset.acWishlistProductId || button.dataset.acWishlistId,
      productId: button.dataset.acWishlistProductId,
      variantId: button.dataset.acWishlistVariantId,
      title: button.dataset.acWishlistTitle,
      variantTitle: button.dataset.acWishlistVariantTitle,
      options: values.map((value, index) => ({ name: names[index] || '', value })),
      url: button.dataset.acWishlistUrl,
      image: button.dataset.acWishlistImage,
      price: button.dataset.acWishlistPrice,
      available: button.dataset.acWishlistAvailable !== 'false',
    });
  }

  #migrate() {
    try {
      if (window.localStorage.getItem(this.#storageKey)) return;
      const legacy = JSON.parse(window.localStorage.getItem(this.#legacyStorageKey) || '[]');
      if (!Array.isArray(legacy) || !legacy.length) return;

      const deduplicated = [];
      for (const item of legacy.map((entry) => this.#normalize(entry))) {
        if (!item.productId || deduplicated.some((saved) => saved.productId === item.productId)) continue;
        deduplicated.push(item);
      }
      window.localStorage.setItem(this.#storageKey, JSON.stringify(deduplicated));
    } catch (error) {
      return;
    }
  }

  #parse(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  #announce(message) {
    document.querySelector('ac-overlay-system')?.announce(message);
  }
}

if (!window.acWishlistStore) window.acWishlistStore = new AcWishlistStore();
